'use client';

import * as React from 'react';
import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from 'lucide-react';
import { DayButton, DayPicker, DropdownProps, getDefaultClassNames } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { buttonVariants } from './utils';

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    formatters,
    components,
    ...props
}: React.ComponentProps<typeof DayPicker> & {
    buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
    const defaultClassNames = getDefaultClassNames();

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn(
                'bg-background light:bg-background dark:bg-background',
                'group/calendar p-3 [--cell-size:--spacing(8)]',
                '[[data-slot=card-content]_&]:bg-transparent',
                '[[data-slot=popover-content]_&]:bg-transparent',
                String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
                String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
                className,
            )}
            captionLayout={captionLayout}
            formatters={{
                formatMonthDropdown: date =>
                    date.toLocaleString('default', { month: 'short' }),
                ...formatters,
            }}
            classNames={{
                root: cn('w-fit', defaultClassNames.root),
                months: cn(
                    'flex gap-4 flex-col md:flex-row relative',
                    defaultClassNames.months,
                ),
                month: cn('flex flex-col w-full gap-4', defaultClassNames.month),
                nav: cn(
                    'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
                    defaultClassNames.nav,
                ),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none',
                    defaultClassNames.button_previous,
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none',
                    defaultClassNames.button_next,
                ),
                month_caption: cn(
                    'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)',
                    'text-foreground light:text-foreground dark:text-foreground',
                    defaultClassNames.month_caption,
                ),
                dropdowns: cn(
                    'w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5',
                    'text-foreground light:text-foreground dark:text-foreground',
                    defaultClassNames.dropdowns,
                ),
                dropdown_root: cn(
                    'relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md',
                    defaultClassNames.dropdown_root,
                ),
                dropdown: cn(
                    'absolute bg-popover inset-0 opacity-0',
                    defaultClassNames.dropdown,
                ),
                caption_label: cn(
                    'select-none font-medium',
                    'text-foreground light:text-foreground dark:text-foreground',
                    captionLayout === 'label'
                        ? 'text-sm'
                        : 'rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5',
                    defaultClassNames.caption_label,
                ),
                table: 'w-full border-collapse',
                weekdays: cn('flex', defaultClassNames.weekdays),
                weekday: cn(
                    'text-muted-foreground light:text-muted-foreground dark:text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none',
                    defaultClassNames.weekday,
                ),
                week: cn('flex w-full mt-2', defaultClassNames.week),
                week_number_header: cn(
                    'select-none w-(--cell-size)',
                    defaultClassNames.week_number_header,
                ),
                week_number: cn(
                    'text-[0.8rem] select-none text-muted-foreground light:text-muted-foreground dark:text-muted-foreground',
                    defaultClassNames.week_number,
                ),
                day: cn(
                    'relative w-full h-full p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none',
                    defaultClassNames.day,
                ),
                range_start: cn(
                    'rounded-l-md bg-accent',
                    defaultClassNames.range_start,
                ),
                range_middle: cn('rounded-none', defaultClassNames.range_middle),
                range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
                today: cn(
                    'data-[selected=false]:bg-accent data-[selected=false]:text-accent-foreground rounded-md',
                    defaultClassNames.today,
                ),
                outside: cn(
                    'text-muted-foreground light:text-muted-foreground dark:text-muted-foreground aria-selected:text-muted-foreground',
                    defaultClassNames.outside,
                ),
                disabled: cn(
                    'text-muted-foreground opacity-50',
                    defaultClassNames.disabled,
                ),
                hidden: cn('invisible', defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Root: ({ className, rootRef, ...props }) => {
                    return (
                        <div
                            data-slot="calendar"
                            ref={rootRef}
                            className={cn(className)}
                            {...props}
                        />
                    );
                },
                Chevron: ({ className, orientation, ...props }) => {
                    const iconClassName = cn(
                        'size-4',
                        'text-foreground light:text-foreground dark:text-foreground',
                        className,
                    );
                    if (orientation === 'left') {
                        return (
                            <ChevronLeftIcon className={iconClassName} {...props} />
                        );
                    }

                    if (orientation === 'right') {
                        return (
                            <ChevronRightIcon
                                className={iconClassName}
                                {...props}
                            />
                        );
                    }

                    return (
                        <ChevronDownIcon className={iconClassName} {...props} />
                    );
                },
                DayButton: CalendarDayButton,
                WeekNumber: ({ children, ...props }) => {
                    return (
                        <td {...props}>
                            <div className="flex size-(--cell-size) items-center justify-center text-center">
                                {children}
                            </div>
                        </td>
                    );
                },
                MonthsDropdown: ({ ...props }) => {
                    return <CustomSelectDropdown {...props} />;
                },
                YearsDropdown: ({ ...props }) => {
                    return <CustomSelectDropdown {...props} />;
                },
                ...components,
            }}
            {...props}
        />
    );
}

function CustomSelectDropdown(props: DropdownProps) {
    const { options, value, onChange } = props;

    const handleValueChange = (newValue: string) => {
        if (onChange) {
            const syntheticEvent = {
                target: {
                    value: newValue,
                },
            } as React.ChangeEvent<HTMLSelectElement>;

            onChange(syntheticEvent);
        }
    };

    return (
        <Select value={value?.toString()} onValueChange={handleValueChange}>
            <SelectTrigger>
                <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[500px]">
                <SelectGroup>
                    {options?.map((option: { value: number; label: string; disabled: boolean }) => (
                        <SelectItem
                            key={option.value}
                            value={option.value.toString()}
                            disabled={option.disabled}
                        >
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectGroup>
            </SelectContent>
        </Select>
    );
}

function CalendarDayButton({
    className,
    day,
    modifiers,
    ...props
}: React.ComponentProps<typeof DayButton>) {
    const defaultClassNames = getDefaultClassNames();

    const ref = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (modifiers.focused) ref.current?.focus();
    }, [modifiers.focused]);

    const isSelected = modifiers.selected
        && !modifiers.range_start
        && !modifiers.range_end
        && !modifiers.range_middle;

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            data-day={day.date.toLocaleDateString()}
            data-selected-single={isSelected}
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            data-today={modifiers.today && !isSelected}
            className={cn(
                // Base text color - muted for outside days (must use prefixes to override ghost variant)
                modifiers.outside
                && 'text-muted-foreground light:text-muted-foreground dark:text-muted-foreground',
                // Selected date styles (takes priority over today) - override base text color
                'data-[selected-single=true]:bg-primary data-[selected-single=true]:!text-primary-foreground data-[selected-single=true]:light:!text-primary-foreground data-[selected-single=true]:dark:!text-primary-foreground',
                // Today styles (only when not selected)
                'data-[today=true]:bg-accent data-[today=true]:text-accent-foreground data-[today=true]:rounded-md',
                // Range styles
                'data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground',
                // Focus and interaction styles
                'group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground',
                // Base styles
                'flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px]',
                // Range border radius
                'data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md',
                '[&>span]:text-xs [&>span]:opacity-70',
                defaultClassNames.day,
                className,
            )}
            {...props}
        />
    );
}

export { Calendar, CalendarDayButton };
